/**
 * The selected recipe as a Tome folio (Tome UI Round 4): profession and level line, what it
 * creates, then each ingredient with owned/required counts and where to find it. A level-locked
 * recipe carries the locked seal. Counts are the inventory's; the craft itself stays the server's.
 */
import React from 'react';
import type { CraftingRecipe } from '../../../../shared/CharacterTypes';
import type { ItemTooltipViewerContext } from '../../components/Tooltip';
import { TomeChapter, TomeChip, TomeFolio } from '../../primitives/tome';
import { CraftingItem } from './CraftingItem';
import { professionLabel, type CraftingRecipeState } from './CraftingRecipeList';
import { CraftingSourceLinks } from './CraftingSources';

export const CraftingRecipeFolio: React.FC<{
  recipe: CraftingRecipe;
  state: CraftingRecipeState;
  owned: ReadonlyMap<string, number>;
  viewer?: ItemTooltipViewerContext;
}> = ({ recipe, state, owned, viewer }) => {
  const requirement = state === 'unknown' ? 'unknown' : state === 'locked' ? 'unmet' : 'met';
  return <TomeFolio className="crafting-tome__folio" aria-label={`${recipe.name} recipe`} shapeSeed={recipe.id}
    seal={state === 'locked' ? 'locked' : undefined} frame="vines">
    <header className="crafting-tome__folio-head">
      <span className="crafting-tome__kicker">{professionLabel(recipe.profession)} recipe</span>
      <h3>{recipe.name}</h3>
      <p className="crafting-tome__requirement" data-requirement-status={requirement}>
        Requires hero level {recipe.requiredLevel}{requirement === 'unknown' ? ' · your level is unavailable'
          : requirement === 'unmet' ? ' · level not met' : ''}</p>
    </header>
    <TomeChapter title="Creates" tone={state === 'ready' ? 'ready' : 'default'}>
      <CraftingItem itemId={recipe.outputItemId} quantity={recipe.outputQuantity} viewer={viewer} />
    </TomeChapter>
    <TomeChapter title="Ingredients" count={recipe.ingredients.length || undefined}>
      {recipe.ingredients.length ? recipe.ingredients.map((ingredient, index) => {
        const have = owned.get(ingredient.itemId) ?? 0;
        const enough = have >= ingredient.quantity;
        return <div key={`${ingredient.itemId}-${index}`} className="crafting-tome__ingredient"
          data-ingredient-id={ingredient.itemId} data-ingredient-status={enough ? 'ready' : 'short'}>
          <CraftingItem itemId={ingredient.itemId} quantity={ingredient.quantity} viewer={viewer}
            caption={<span className="crafting-tome__owned">
              <TomeChip tone={enough ? 'completed' : 'available'} cut="clean">{enough ? 'Have' : 'Short'}</TomeChip>
              <span>Owned {have} / Required {ingredient.quantity}{enough ? ' · ready' : ` · need ${ingredient.quantity - have} more`}</span>
            </span>}>
            <CraftingSourceLinks itemId={ingredient.itemId} />
          </CraftingItem>
        </div>;
      }) : <p className="crafting-tome__note">No ingredients required.</p>}
    </TomeChapter>
  </TomeFolio>;
};
